import React from 'react';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import InnerHTML from '@Components/InnerHTML';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import CitationCell from '@Components/Results/substances/tables/cells/citation-cell.component';
import TargetCell from '@Components/Results/substances/tables/cells/TargetCell';
import NumberFormatter from '@Utils/NumberFormatter';
import { CITATION_LINKS_TRACKING_INDEX_MARKER } from '@Components/Results/analytics/citationLinksTracking';

function renderBasicDataRow(title, data) {
  return (
    data && (
      <tr className="table__row c-els-table__row">
        <th
          className="table__cell c-els-table__cell c-els-table__basic-cell table__cell--header c-els-table__cell--header qualitative-information__label-cell"
          scope="row">
          {title}
        </th>
        <InnerHTML
          className="table__cell c-els-table__cell c-els-table__basic-cell qualitative-information__value-cell"
          tag="td"
          html={data}
        />
      </tr>
    )
  );
}

function renderMeasurementsDataRow(title, data) {
  const measurements = [];
  if (data && data.length) {
    data.forEach((measurement) => {
      if (measurement.obj) {
        measurements.push(measurement.obj);
      }
    });
  }
  return measurements.length ? (
    <tr className="table__row c-els-table__row">
      <th
        className="table__cell c-els-table__cell c-els-table__basic-cell table__cell--header c-els-table__cell--header qualitative-information__label-cell"
        scope="row">
        {title}
      </th>
      <InnerHTML
        className="table__cell c-els-table__cell c-els-table__basic-cell qualitative-information__value-cell"
        tag="td"
        html={measurements.join(', ')}
      />
    </tr>
  ) : null;
}

function SubstanceQualitativeTable({ metadata, data, total }) {
  return (
    <div className="qualitative-information">
      {map(data, (item, index) => {
        const resultPosition = `${NumberFormatter(index + 1, 0)} of ${NumberFormatter(total, 0)}`;
        const details = item?.details || {};

        return (
          <div key={item?.id || index} className="qualitative-information__item">
            <div className="qualitative-information__count">{resultPosition}</div>

            <div className="qualitative-information__table-wrap">
              <table className="qualitative-information__table table c-els-table c-els-table--subtle-dividers">
                <caption className="c-els-table__caption u-els-hide-visually">Qualitative result {resultPosition}</caption>
                <tbody className="table__body c-els-table__body">
                  {renderBasicDataRow('Effect', details.effect)}
                  {details.targets && (
                    <tr className="table__row c-els-table__row">
                      <th
                        className="table__cell c-els-table__cell c-els-table__basic-cell table__cell--header c-els-table__cell--header qualitative-information__label-cell"
                        scope="row">
                        Target
                      </th>
                      <td className="table__cell c-els-table__cell c-els-table__basic-cell qualitative-information__value-cell">
                        <TargetCell metadata={metadata} value={details.targets} />
                      </td>
                    </tr>
                  )}
                  {renderBasicDataRow('Substance action on target', details.targetAction)}
                  {(details.bioSpecies || details.tissueOrgan || details.cell) && (
                    <tr className="table__row c-els-table__row">
                      <th
                        className="table__cell c-els-table__cell c-els-table__basic-cell table__cell--header c-els-table__cell--header qualitative-information__label-cell"
                        scope="row">
                        Biological material
                      </th>
                      <td className="table__cell c-els-table__cell c-els-table__basic-cell qualitative-information__value-cell">
                        {details.bioSpecies && <div>{details.bioSpecies.join(' + ')}</div>}
                        {details.tissueOrgan && <div>{details.tissueOrgan.join(' + ')}</div>}
                        {details.cell && <div>{details.cell.join(' + ')}</div>}
                      </td>
                    </tr>
                  )}
                  {renderBasicDataRow('Assay Description', details.assayDescription)}
                  {renderBasicDataRow('Results', details.results)}
                  {details.citations && (
                    <tr className="table__row c-els-table__row" {...CITATION_LINKS_TRACKING_INDEX_MARKER.substancesTableItem}>
                      <th
                        className="table__cell c-els-table__cell c-els-table__basic-cell table__cell--header c-els-table__cell--header qualitative-information__label-cell"
                        scope="row">
                        Reference
                      </th>
                      <td className="table__cell c-els-table__cell c-els-table__basic-cell qualitative-information__value-cell">
                        <CitationCell metadata={metadata} citations={details.citations} />
                      </td>
                    </tr>
                  )}
                  {renderMeasurementsDataRow('Measurement', item?.measurements)}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

SubstanceQualitativeTable.propTypes = {
  metadata: MetadataPropTypesShape,
  data: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
};

export default SubstanceQualitativeTable;
